import { inject } from '@angular/core';
import {
  required,
  minLength,
  maxLength,
  min,
  max,
  email,
  pattern,
  validate,
} from '@angular/forms/signals';
import { TranslateService } from '@ngx-translate/core';

export function requiredValidator(path: any): void {
  const translate = inject(TranslateService);
  required(path, {
    message: (): string => translate.instant('Validation.Required'),
  });
}

export function maxLengthValidator(path: any, length: number): void {
  const translate = inject(TranslateService);
  maxLength(path, length, {
    message: (): string =>
      translate.instant('Validation.MaxLength', { max: length }),
  });
}

export function minLengthValidator(path: any, length: number): void {
  const translate = inject(TranslateService);
  minLength(path, length, {
    message: (): string =>
      translate.instant('Validation.MinLength', { min: length }),
  });
}

export function minValidator(path: any, value: number): void {
  const translate = inject(TranslateService);
  min(path, value, {
    message: (): string => translate.instant('Validation.Min', { min: value }),
  });
}

export function maxValidator(path: any, value: number): void {
  const translate = inject(TranslateService);
  max(path, value, {
    message: (): string => translate.instant('Validation.Max', { max: value }),
  });
}

export function emailValidator(path: any): void {
  const translate = inject(TranslateService);
  email(path, {
    message: (): string => translate.instant('Validation.Email'),
  });
}

/**
 * Rejects date/datetime values later than `max()`, evaluated fresh on every
 * validation run (unlike a static `props.max` on the picker, which only bounds
 * calendar navigation and never surfaces a message if a later value gets in
 * some other way, e.g. free text entry).
 */
export function maxDateValidator(path: any, max: () => Date): void {
  const translate = inject(TranslateService);
  validate(path, ({ value }) => {
    const raw = value();
    if (!raw) return undefined;
    const current = new Date(raw as unknown as string);
    if (Number.isNaN(current.getTime())) return undefined;
    if (current.getTime() <= max().getTime()) return undefined;
    return {
      kind: 'maxDate',
      message: translate.instant('Validation.MaxDate'),
    };
  });
}

/** Rejects date/datetime values later than the current moment. */
export function notInFutureValidator(path: any): void {
  maxDateValidator(path, () => new Date());
}

export function patternValidator(
  path: any,
  regex: RegExp,
  keySuffix = 'Pattern'
): void {
  const translate = inject(TranslateService);
  pattern(path, regex, {
    message: (): string => translate.instant(`Validation.${keySuffix}`),
  });
}
